import React, { Component, Fragment } from 'react'
import { Layout } from 'app/common/layout'
import styled, { css } from 'styled-components'
import { fonts, H1, Text } from 'app/common/typography'
import { FormattedMessage, injectIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import {
    breakpoints,
    media,
    ResponsiveContainer,
    style
} from 'app/assets/styles'
import { Breadcrumb } from 'app/common/breadcrumb'
import { ErrorManagement } from 'app/common/error-management'
import type { FormDelivery, RestaurantShortDTO } from 'shared/types'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme'
import { Field, Form, Formik } from 'formik'
import { object, string } from 'yup'
import { Error } from 'app/common/forms'
import type { Coordinates } from 'shared/store/store-locator'
import BlueBackground from 'shared/assets/images/blue-background.png'
import RedBackground from 'shared/assets/images/red-background.jpg'
import BlackBackground from 'shared/assets/images/black-background.png'
import Whopper from 'shared/assets/images/whopper-delivery.png'
import WhopperDeliveroo from 'app/assets/images/delivery/whopper-deliveroo.png'
import WhopperUberEats from 'app/assets/images/delivery/whopper-ubereats.png'
import EmojiSlime from 'shared/assets/images/emoji-slime.png'
import EmojiDisappointed from 'shared/assets/images/emoji-disappointed.png'
import { restaurantServices } from 'shared/utils'
import { eventsDefinitions } from 'shared/store/metadata/events-definitions'
import type { EventMetadata } from 'shared/store/metadata'
import { StorePush } from 'app/common/store-push'
import { DeliverooLogo, UberEats } from 'app/assets/icons'
import { InputAddressAutocomplete } from 'app/store-locator/input-address-autocomplete'
import { BORDER, COLOR } from 'app-v2/design-tokens'

const { delivery, shadowBox } = oldColors
const { EXTERNAL_DELIVERY, DELIVEROO, UBEREATS } = restaurantServices

type Props = {
    fetchDeliveryPageData: () => void,
    sendMeta: EventMetadata => void,
    reset: () => void,
    error: ?any,
    networkError: ?any,
    intl: IntlShape,
    dataLoaded: boolean,
    error: any,
    restaurants: Array<RestaurantShortDTO>,
    sendEmailError: string,
    sendEmailSuccess: boolean,
    sendEmail: FormDelivery => void,
    address: string,
    center: Coordinates
}

type State = {
    width: number
}

const validationSchema = object().shape({
    email: string()
        .email()
        .required()
})

class DeliveryOptions extends Component<Props, State> {
    state = { width: 0 }

    componentDidMount() {
        this.props.fetchDeliveryPageData()
        this.updateWindowDimensions()
        window.addEventListener('resize', this.updateWindowDimensions)
        /* Tracking Syzmek : 1346587 = landing page livraison */
        if (window.mmConversionTag) {
            window.mmConversionTag(1346587, this)
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions)
        this.props.reset()
    }

    updateWindowDimensions = () => {
        this.setState({ width: window.innerWidth })
    }

    sendMetadata = (action: string) => {
        const { sendMeta } = this.props
        const meta = {
            ...eventsDefinitions.DELIVERY,
            event_action: action
        }
        sendMeta(meta)
    }

    render() {
        const {
            intl,
            error,
            networkError,
            restaurants,
            sendEmailError,
            sendEmailSuccess
        } = this.props

        const isMedium = this.state.width > breakpoints.medium

        const sendFormEmail = (email: string) => {
            const { sendEmail, address, center } = this.props
            const form = {
                email: email,
                address: address,
                zipCode: '',
                city: '',
                lng: center.lng,
                lat: center.lat
            }
            sendEmail(form)
        }

        let background = RedBackground
        let whopper = Whopper
        let deliveroo = false
        let ubereats = false

        if (!!restaurants && restaurants.length > 0) {
            deliveroo = restaurants[0].services.includes(DELIVEROO)
            ubereats = restaurants[0].services.includes(UBEREATS)

            if (deliveroo && !ubereats) {
                background = BlueBackground
                whopper = WhopperDeliveroo
            } else if (ubereats && !deliveroo) {
                background = BlackBackground
                whopper = WhopperUberEats
            }
        }
        const numberOfDeliveryTypes = deliveroo + ubereats

        return (
            <ErrorManagement error={error} networkError={networkError}>
                <Layout>
                    <RightCol number={numberOfDeliveryTypes}>
                        <div>
                            <DeliveryBackground
                                src={background}
                                number={numberOfDeliveryTypes}
                            />
                            {(isMedium || !restaurants) && (
                                <WhopperDeliverooImg src={whopper} />
                            )}
                        </div>
                    </RightCol>
                    <LeftCol>
                        <MainContainer>
                            <BreadcrumbContainer>
                                <Breadcrumb
                                    breadcrumbArray={[
                                        {
                                            title: intl.formatMessage({
                                                id:
                                                    'delivery.title.breadcrumb.end'
                                            })
                                        }
                                    ]}
                                />
                            </BreadcrumbContainer>
                            <Title>
                                <FormattedMessage id="delivery.title" />
                            </Title>
                            <Subtitle>
                                <FormattedMessage id="delivery.subtitle" />
                            </Subtitle>
                            <AdressSearchContainer>
                                <InputAddressAutocomplete
                                    filters={[EXTERNAL_DELIVERY]}
                                    radius={2000}
                                    delivery
                                />
                            </AdressSearchContainer>
                            {restaurants &&
                                (restaurants.length > 0 ? (
                                    <Fragment>
                                        <EmojiWrapper>
                                            <EmojiImage src={EmojiSlime} />
                                            <ResponseText>
                                                <FormattedMessage id="delivery.success" />
                                            </ResponseText>
                                        </EmojiWrapper>
                                        <DeliveryApps>
                                            {deliveroo && (
                                                <StorePush
                                                    playStoreUrlKey="delivery.deliveroo.app.android.url"
                                                    appleStoreUrlKey="delivery.deliveroo.app.ios.url"
                                                    linkUrl={
                                                        !!restaurants[0]
                                                            .deliverooUrl
                                                            ? restaurants[0]
                                                                  .deliverooUrl
                                                            : intl.formatMessage(
                                                                  {
                                                                      id:
                                                                          'delivery.deliveroo.url'
                                                                  }
                                                              )
                                                    }
                                                    buttonKey="storelocator.restaurant.button.delivery"
                                                    handleClick={() =>
                                                        this.sendMetadata(
                                                            'DELIVEROO'
                                                        )
                                                    }
                                                    color={
                                                        isMedium
                                                            ? delivery
                                                            : 'white'
                                                    }
                                                    icon={
                                                        <DeliverooLogo
                                                            height={
                                                                isMedium
                                                                    ? 50
                                                                    : 35
                                                            }
                                                            color={
                                                                isMedium
                                                                    ? delivery
                                                                    : 'white'
                                                            }
                                                        />
                                                    }
                                                />
                                            )}
                                            {ubereats && (
                                                <StorePush
                                                    playStoreUrlKey="delivery.ubereats.app.android.url"
                                                    appleStoreUrlKey="delivery.ubereats.app.ios.url"
                                                    linkUrl={
                                                        !!restaurants[0]
                                                            .uberEatsUrl
                                                            ? restaurants[0]
                                                                  .uberEatsUrl
                                                            : intl.formatMessage(
                                                                  {
                                                                      id:
                                                                          'delivery.ubereats.url'
                                                                  }
                                                              )
                                                    }
                                                    buttonKey="storelocator.restaurant.button.delivery"
                                                    color={
                                                        isMedium
                                                            ? 'black'
                                                            : 'white'
                                                    }
                                                    handleClick={() =>
                                                        this.sendMetadata(
                                                            'UBER EATS'
                                                        )
                                                    }
                                                    icon={
                                                        <UberEatsImage
                                                            height={
                                                                isMedium
                                                                    ? 25
                                                                    : 15
                                                            }
                                                            color={
                                                                isMedium
                                                                    ? undefined
                                                                    : 'white'
                                                            }
                                                        />
                                                    }
                                                />
                                            )}
                                        </DeliveryApps>
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <EmojiWrapper>
                                            <EmojiImage
                                                src={EmojiDisappointed}
                                            />
                                            <ResponseText>
                                                <FormattedMessage id="delivery.fail" />
                                            </ResponseText>
                                        </EmojiWrapper>
                                        <EmailTitle
                                            font={fonts.roboto}
                                            color="black">
                                            <FormattedMessage id="delivery.form.email.title" />
                                        </EmailTitle>

                                        <Formik
                                            initialValues={{
                                                email: ''
                                            }}
                                            onSubmit={(values, actions) => {
                                                sendFormEmail(values.email)
                                                values.email = ''
                                                actions.setSubmitting(false)
                                            }}
                                            validationSchema={validationSchema}
                                            render={({
                                                values,
                                                errors,
                                                touched,
                                                isSubmitting
                                            }) => (
                                                <FormWithWidth noValidate>
                                                    <FormRow>
                                                        <FormControl>
                                                            <StyledField
                                                                type="email"
                                                                name="email"
                                                                spellCheck={
                                                                    false
                                                                }
                                                                maxLength={120}
                                                                placeholder={intl.formatMessage(
                                                                    {
                                                                        id:
                                                                            'delivery.form.email.placeholder'
                                                                    }
                                                                )}
                                                            />
                                                            <InlineSubmit
                                                                type="submit"
                                                                disabled={
                                                                    isSubmitting
                                                                }>
                                                                <FormattedMessage id="delivery.form.email.submit" />
                                                            </InlineSubmit>
                                                        </FormControl>
                                                        {errors.email &&
                                                            touched.email && (
                                                                <Error
                                                                    errorKey="form.error.field.mandatory"
                                                                    color={
                                                                        isMedium
                                                                            ? oldColors.error
                                                                            : 'white'
                                                                    }
                                                                />
                                                            )}
                                                        {sendEmailSuccess && (
                                                            <Error
                                                                errorKey="delivery.form.email.success"
                                                                color={
                                                                    colors.crunchyGreen
                                                                }
                                                            />
                                                        )}
                                                        {sendEmailError && (
                                                            <Error
                                                                errorKey="form.error.generic.message"
                                                                color={
                                                                    isMedium
                                                                        ? oldColors.error
                                                                        : 'white'
                                                                }
                                                            />
                                                        )}
                                                    </FormRow>
                                                </FormWithWidth>
                                            )}
                                        />
                                    </Fragment>
                                ))}
                        </MainContainer>
                    </LeftCol>
                </Layout>
            </ErrorManagement>
        )
    }
}

const col = css`
    width: 100%;
    ${media.medium`
        width: 50%;
    `};
`

const LeftCol = styled.div`
    ${col};
    position: absolute;
    top: 0;
    left: 0;
`

const RightCol = styled.div`
    ${col};
    height: ${props => {
        switch (props.number) {
            case 2:
                return '830px'
            case 3:
                return '1050px'
            default:
                return '700px'
        }
    }};
    ${media.medium`
        height: ${props => (props.number > 2 ? '900px' : '700px')};
    `};
`

const DeliveryBackground = styled.img`
    width: 100%;
    height: ${props => {
        switch (props.number) {
            case 2:
                return '830px'
            case 3:
                return '1050px'
            default:
                return '700px'
        }
    }};
    position: absolute;
    ${media.medium`
        width: 50%;
        left: 50%;
        height: ${props => (props.number > 2 ? '900px' : '700px')};
    `};
    background-color: #00ccbc;
`

const EmojiWrapper = styled.div`
    ${media.medium`
    display: flex;
    `};
`

const EmojiImage = styled.img`
    width: 50px;
    height: 50px;
`

const WhopperDeliverooImg = styled.img`
    width: 70%;
    max-width: 350px;
    position: absolute;
    z-index: 9;
    margin-left: auto;
    margin-right: auto;
    left: 0;
    right: 0;
    top: 390px;
    ${media.medium`
        display: block;
        left:52%;
        top: 240px;
        max-width: 480px;
    `};
`

const UberEatsImage = styled(UberEats)`
    margin: 10px 0;
    ${media.medium`
        margin-top: 15px;
    `};
`

const MainContainer = styled(ResponsiveContainer)`
    margin-top: 80px;
    margin-bottom: 20px;
    text-align: center;
    ${media.medium`
        margin-top: 90px;
        margin-bottom: 25px;
        width: calc(100% - 40px);
        text-align: left;
    `};
    ${media.large`
        width: calc(100% - 40px - (100vw - 1280px) / 2);
        margin-left: calc((100vw - 1280px) / 2);
    `};
`

const BreadcrumbContainer = styled.div`
    display: none;
    ${media.medium`
        postion: absolute;
        text-align: left;
        display: block;
    `};
`

const AdressSearchContainer = styled.div`
    margin: 30px 0 45px 0;
    text-align: left;
    ${media.medium`
        width: 90%;
    `};
`

const Title = styled(H1)`
    text-align: left;
    color: white;
    margin-bottom: 5px;
    ${media.medium`
        color: inherit;
    `};
`

const StyledText = styled(Text)`
    color: white;
    display: block;
    ${media.medium`
        color: inherit;
    `};
`

const ResponseText = styled(StyledText)`
    margin: 0 10%;
    ${media.medium`
    line-height: 45px;
    margin: 0 20px;
    `};
`

const Subtitle = styled(StyledText)`
    text-align: left;
`

const EmailTitle = styled(StyledText)`
    margin-top: 30px;
`

const FormWithWidth = styled(Form)`
    width: 100%;
`

const FormRow = styled.div`
    max-width: 270px;
    display: flex;
    justify-content: flex-start;
    flex-direction: column;
    margin: 10px auto 20px;
    ${media.medium`
        margin-left: 0;
        margin-right: 0;
    `};
`

const FormControl = styled.div`
    position: relative;
    display: inline-block;
    width: 270px;
    ${media.medium`
        display: block;
    `};
`

const StyledField = styled(Field)`
    -webkit-appearance: none;
    background-color: rgba(255, 255, 255, 0.5);
    color: white;
    ${media.medium`
        background-color: ${COLOR.SECONDARY};
        color: black;
    `};
    box-shadow: 0 4px 16px 4px ${shadowBox};
    outline: none;
    border: none;
    border-radius: ${BORDER.RADIUS.M};
    font-family: 'Roboto';
    font-style: italic;
    font-weight: 300;
    font-size: 16px;
    line-height: 18px;
    padding: 8px 56px 8px 16px;
    box-sizing: border-box;
    width: 100%;
    height: 40px;
    &:-webkit-autofill {
        background-color: green !important;
    }
    &::-webkit-input-placeholder {
        color: white;
        ${media.medium`
        color: black;
    `};
    }
    &::-moz-placeholder {
        color: white;
        ${media.medium`
        color: black;
    `};
    }
    &:-ms-input-placeholder {
        color: white;
        ${media.medium`
        color: black;
    `};
    }
    &:-moz-placeholder {
        color: white;
        ${media.medium`
        color: black;
    `};
    }
`

const InlineSubmit = styled.button`
    ${style.button};
    position: absolute;
    padding: 8px 16px;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    color: white;
    font-size: 20px;
    line-height: 20px;
    font-family: ${fonts.roboto};
    ${media.medium`
        color: black;
    `};
    &[disabled] {
        opacity: 0.5;
    }
`

const DeliveryApps = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 30px;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
`

const DeliveryOptionsComponent = injectIntl(DeliveryOptions)

export { DeliveryOptionsComponent }
